import { BaseExercise, Exercise, MainExercise } from "@domain/exercise/exercise";
import { Filter, FilterCategory } from "@domain/filters/filter";
import { isNotNil } from "@utils/assert";
import { isWordMatchingSearch } from "@utils/string-utils";
import { Observable, observable } from "micro-observables";
import { ExerciseManager } from "@domain/exercise/exercise-manager";
import { ExerciseSelectionManager } from "@domain/exercise/exercise-selection-manager";

export enum LibraryFilteringState {
	UNDEFINED = "undefined",
	COMPLEMENTARY = "complementary",
	ALTERNATIVE = "alternative",
}

export class LibraryFilterManager extends ExerciseSelectionManager {
	private _exercises: BaseExercise[] = [];

	private readonly _filteredExercises = observable<BaseExercise[]>(this._exercises);
	filteredExercises = this._filteredExercises.readOnly();

	private readonly _filteredSelectedExercises = observable<BaseExercise[]>([]);
	filteredSelectedExercises = this._filteredSelectedExercises.readOnly();

	private readonly _sidebarVisible = observable<boolean>(true);
	sidebarVisible = this._sidebarVisible.readOnly();

	private readonly _filteringState = observable<LibraryFilteringState>(LibraryFilteringState.UNDEFINED);
	filteringState = this._filteringState.readOnly();

	exerciseWithAlternativesEditedList: Observable<Exercise[]>;

	private readonly _lastAddedExerciseData = observable<
		| {
				draggableId: string;
				isComplementary: boolean;
				isAlternative: boolean;
		  }
		| undefined
	>(undefined);
	lastAddedExerciseMetadata = this._lastAddedExerciseData.readOnly();

	searchWord = observable<string>("");
	activeCategoryFilters = observable<FilterCategory<ExerciseFilter>[]>([]);
	exerciseWithAlternativesEdited = observable<MainExercise | null>(null);

	constructor(private readonly exerciseManager: ExerciseManager) {
		super();

		this.searchWord.subscribe(() => this.filterExercises());
		this.activeCategoryFilters.subscribe(() => this.filterExercises());
		this.exerciseWithAlternativesEdited.subscribe(() => this.filterExercises());
		this.selectedExercises.subscribe(() => this.filterExercises());

		this.exerciseWithAlternativesEditedList = this.exerciseWithAlternativesEdited.select(editedExercise => {
			const collator = new Intl.Collator();
			if (isNotNil(editedExercise)) {
				return [editedExercise, ...editedExercise.alternativeExercises].sort((exercise1, exercise2) =>
					collator.compare(exercise1.title, exercise2.title),
				);
			}
			return [];
		});
	}

	setSidebarVisible(visible: boolean): void {
		this._sidebarVisible.set(visible);
	}

	async listAllExercises(): Promise<void> {
		await this.exerciseManager.fetchExercises();
		this._exercises = this.exerciseManager.allExercises.get();
		this.filterExercises();
	}

	async clearLibraryFiltersAndSearch(): Promise<void> {
		this._filteredExercises.set([]);
		this.activeCategoryFilters.set([]);
		this.searchWord.set("");
		this.exerciseWithAlternativesEdited.set(null);
		this._filteringState.set(LibraryFilteringState.UNDEFINED);
	}

	async reset(): Promise<void> {
		this._exercises = [];
		this.clearLibraryFiltersAndSearch();
	}

	setIsComplementaryExerciseActive(): void {
		this._filteringState.set(LibraryFilteringState.COMPLEMENTARY);
	}

	setIsAlternativeExerciseActive(): void {
		this._filteringState.set(LibraryFilteringState.ALTERNATIVE);
	}

	clearActiveFilteringState(): void {
		this._filteringState.set(LibraryFilteringState.UNDEFINED);
	}

	setLastAddedExerciseMetadata(newExerciseId: string, isAlternative = false): void {
		this._lastAddedExerciseData.set({
			draggableId: newExerciseId,
			isComplementary: this.filteringState.get() === LibraryFilteringState.COMPLEMENTARY,
			isAlternative,
		});
	}
    
	clearLastAddedExerciseMetadata(): void {
		this._lastAddedExerciseData.set(undefined);
	}

	setExerciseWithAlternativesEdited(newExercise: MainExercise): void {
		this.exerciseWithAlternativesEdited.set(newExercise);
	}

	selectExercises(exercises: BaseExercise[]): void {
		this.updateSelectedExerciseList(exercises, true);
	}

	private filterExercises(): void {
		this._filteredSelectedExercises.set(this.getMatchingExercises(this.selectedExercises.get()));

		const exercisesWithoutSelected = this._exercises.filter(
			exercise => !this.selectedExercises.get().some(e => exercise.id === e.id)
		);
		
		const filteredExercises = this.getMatchingExercises(exercisesWithoutSelected);
		this._filteredExercises.set(filteredExercises);
	}

	private getMatchingExercises(exercises: BaseExercise[]): BaseExercise[] {
		const filteredByCategoryExercises = exercises.filter(exercise => {
			for (const category of this.activeCategoryFilters.get()) {
				if (!this.exerciseMatchFilterCategory(exercise, category)) {
					return false;
				}
			}
			return true;
		});

		const search = this.searchWord.get();

		const filteredByNameExercises = filteredByCategoryExercises.filter(
			exercise => !search || isWordMatchingSearch(exercise.title, search),
		);

		const filteredByTagsRemainingExercises = filteredByCategoryExercises.filter(
			exercise =>
				(!search || exercise.tags.find(tag => isWordMatchingSearch(tag, search))) &&
				!filteredByNameExercises.find(e => e.id === exercise.id),
		);

		const filteredBtNameAndTagsExercises = [...filteredByNameExercises, ...filteredByTagsRemainingExercises];
		const alreadySelectedExercises = this.exerciseWithAlternativesEditedList.get();

		return filteredBtNameAndTagsExercises.filter(
			filteredExercise => !alreadySelectedExercises.some(e => filteredExercise.id === e.id),
		);
	}

	private exerciseMatchFilterCategory(exercise: Exercise, filterCategory: FilterCategory<ExerciseFilter>): boolean {
		if (filterCategory.filters.length === 0) {
			return true;
		}

		for (const filter of filterCategory.filters) {
			if (filter.predicate(exercise)) {
				return true;
			}
		}
		return false;
	}
}

export interface ExerciseFilter extends Filter {
	predicate: (exercise: Exercise) => boolean;
}
